import React, {useEffect, useRef, useState} from 'react';
import {View} from 'react-native';
import {PatternFormat} from 'react-number-format';
import {observer} from 'mobx-react-lite';

import BackButton from '../../components/rewards/BackButton';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RewardsSignUpTypeParams, RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {navigate} from '../../navigation/RootNavigation';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';

const RewardsWelcomeBackScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_WELCOME_BACK>) => {
    const {rewardsStore} = useStores();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendCodeError, setSendCodeError] = useState<string | null>(null);
    const mountedRef = useRef(true);
    const {flowType} = route.params || ({} as RewardsSignUpTypeParams);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const handleSendCode = async () => {
      setIsSubmitting(true);
      setSendCodeError(null);
      try {
        const result = await apiService.rewardsVerifyExistingUserPhoneNumber({
          phoneNumber: rewardsStore.phoneNumber,
        });

        if (!mountedRef.current) return;

        if (result?.status === 200) {
          navigate(AppRoute.REWARDS_VERIFICATION, {flowType});
        } else {
          setSendCodeError('Could not send verification code. Please try again.');
        }
      } catch (_error) {
        if (!mountedRef.current) return;
        setSendCodeError('Could not send verification code. Please try again.');
      } finally {
        if (mountedRef.current) {
          setIsSubmitting(false);
        }
      }
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <BackButton
            onPress={() => {
              navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
            }}
          />
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">LOOKS LIKE YOU HAVE AN ACCOUNT</Text>
          </View>
          <Text>
            It appears you already have an account with {config.APP_NAME}.{'\n\n'}
            So we can confirm we have the right details for you, we will need to verify your mobile
            number by sending you a verification code.
          </Text>

          <PatternFormat
            disabled
            containerStyle={{marginTop: 36}}
            format="(+61) ##########"
            allowEmptyFormatting
            customInput={TextInput}
            // Phone number without country code or area code
            value={rewardsStore.phoneNumber?.replace(/^\+61/, '') || ''}
            accessibilityLabel="Your mobile number:"
            autoCapitalize="none"
            autoComplete="tel"
            keyboardType="phone-pad"
            label="Your mobile number:"
            maxLength={20}
          />
          <View style={{gap: 8}}>
            {sendCodeError ? (
              <Text
                variant="body2"
                color="error"
                style={{marginVertical: 8}}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
                testID="welcome-back-send-code-error">
                {sendCodeError}
              </Text>
            ) : null}
            <Button
              title={'SEND CODE'}
              onPress={handleSendCode}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsWelcomeBackScreen;
